import { Box, CheckboxInput, FormikSelectInputNative, FormikTextInput } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ChangeEvent, useCallback, useMemo } from 'react';

import {
  AdditionalSignerRole,
  ContractDocumentProviders,
  FinanceType,
  FinanceTypeLabels,
  ProgramType,
  ProgramTypeLabels,
} from 'types';

import { getStateOptions } from '../../helpers/getStateOptions';

import { ContractTemplateFormValues, VaultChoice } from './contractTemplateSchema';
import { DocuSignTemplateIdField } from './DocuSignTemplateIdField';

const PUERTO_RICO_STATE = 'PR';
const VAULT_YES: VaultChoice = 'yes';

const PROVIDER_OPTIONS = [
  { label: 'DocuSign', value: ContractDocumentProviders.docusign },
  { label: 'Conga', value: ContractDocumentProviders.conga },
];

const VAULT_OPTIONS = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

const SIGNER_OPTIONS: Array<{ role: AdditionalSignerRole; label: string }> = [
  { role: AdditionalSignerRole.salesRep, label: 'Sales Rep' },
  { role: AdditionalSignerRole.epcInstallerRep, label: 'EPC/Installer Rep' },
];

const SPANISH_AVAILABLE_HELP = 'Offer this contract in Spanish';
const UNUSED_TEMPLATE_HELP = 'Not used for Conga — kept so switching back is instant';

// maxLength is inert on a number input, and TextInput only forwards unrecognized attributes to the
// element through inputProps.
const LSE_ID_INPUT_PROPS = { min: 1, max: 99999 };

const ContractTemplateHeader = () => (
  <Box childGap="sm">
    <Box fontWeight="bold">Contract Provider and Templates</Box>
    <Box>
      Choose the e-signature provider and the signers each contract needs. State must be an active market in order to
      configure contracts. For DocuSign, add all applicable templates; if no contract exists (e.g. not offering
      Spanish), leave blank.
    </Box>
    <Box>Changes apply to contracts generated from now on, not to contracts that were already sent.</Box>
  </Box>
);

interface SignerCheckboxProps {
  role: AdditionalSignerRole;
  label: string;
  className?: string;
}

const SignerCheckbox = ({ role, label, className }: SignerCheckboxProps) => {
  const { values, setFieldValue } = useFormikContext<ContractTemplateFormValues>();
  const isChecked = values.requiredAdditionalSigners.includes(role);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const without = values.requiredAdditionalSigners.filter((r) => r !== role);
      void setFieldValue('requiredAdditionalSigners', event.target.checked ? [...without, role] : without);
    },
    [role, setFieldValue, values.requiredAdditionalSigners],
  );

  return (
    <Box className={className}>
      <CheckboxInput id={`signer-${role}`} label={label} isChecked={isChecked} onChange={handleChange} />
    </Box>
  );
};

interface GroupProps {
  className?: string;
}

const AdditionalSignersField = ({ className }: GroupProps) => (
  <Box className={className} childGap="sm">
    <Box fontWeight="bold">Required additional signers</Box>
    <Box childGap="md">
      {SIGNER_OPTIONS.map(({ role, label }) => (
        <SignerCheckbox key={role} role={role} label={label} />
      ))}
    </Box>
  </Box>
);

const CongaSettingsFields = ({ className }: GroupProps) => {
  const { values, setFieldValue } = useFormikContext<ContractTemplateFormValues>();
  const isPuertoRico = values.state === PUERTO_RICO_STATE;

  const handleSpanishChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setFieldValue('congaSpanishAvailable', event.target.checked),
    [setFieldValue],
  );

  return (
    <Box className={className} childGap="lg">
      <Box fontWeight="bold">Conga settings</Box>
      <Box>
        <CheckboxInput
          id="congaSpanishAvailable"
          label="Spanish available"
          isChecked={isPuertoRico || values.congaSpanishAvailable}
          isDisabled={isPuertoRico}
          helpText={isPuertoRico ? 'Always' : SPANISH_AVAILABLE_HELP}
          onChange={handleSpanishChange}
        />
      </Box>
      <Field
        label="Vault on completion"
        name="vaultOnCompletion"
        id="vaultOnCompletion"
        options={VAULT_OPTIONS}
        component={FormikSelectInputNative}
        isRequired
      />
      {values.vaultOnCompletion === VAULT_YES && (
        <Field
          type="text"
          label="eOriginal Lender"
          name="eOriginalLender"
          id="eOriginalLender"
          component={FormikTextInput}
          isRequired
        />
      )}
    </Box>
  );
};

export const ContractTemplateFormFields = () => {
  const { congaContractProvider = false } = useFlags();
  const { values } = useFormikContext<ContractTemplateFormValues>();
  const isConga = values.provider === ContractDocumentProviders.conga;

  // A row already on Conga must keep showing it even if the flag is turned off afterwards.
  const providerOptions = useMemo(
    () => (congaContractProvider || isConga ? PROVIDER_OPTIONS : PROVIDER_OPTIONS.slice(0, 1)),
    [congaContractProvider, isConga],
  );

  const templateHelp = isConga ? UNUSED_TEMPLATE_HELP : undefined;

  return (
    <Box childGap={{ base: 'lg', desktop: 'xl' }}>
      <ContractTemplateHeader />
      <Field
        id="programType"
        name="programType"
        label="Program Type"
        options={Object.values(ProgramType).map((pt) => ({
          label: ProgramTypeLabels[pt],
          value: pt,
        }))}
        component={FormikSelectInputNative}
        isRequired
        isDisabled
      />

      <Field
        type="text"
        label="State"
        name="state"
        id="state"
        options={getStateOptions(true)}
        component={FormikSelectInputNative}
        isRequired
      />

      <Field
        inputProps={LSE_ID_INPUT_PROPS}
        type="number"
        label="Load Serving Entity (LSE) ID"
        name="lseId"
        id="lseId"
        component={FormikTextInput}
        placeholder="e.g. 734"
      />

      <Field
        type="text"
        label="Contract Type"
        name="contractType"
        id="contractType"
        options={Object.values(FinanceType).map((type) => ({
          label: FinanceTypeLabels[type],
          value: type,
        }))}
        component={FormikSelectInputNative}
        isRequired
      />

      <Field
        label="Provider"
        name="provider"
        id="provider"
        options={providerOptions}
        component={FormikSelectInputNative}
        isRequired
      />

      <AdditionalSignersField />

      {isConga && <CongaSettingsFields />}

      <DocuSignTemplateIdField
        label="English Template ID"
        name="englishTemplateId"
        id="englishTemplateId"
        isRequired={!isConga}
        isDisabled={isConga}
        helpText={templateHelp}
      />

      <DocuSignTemplateIdField
        label="English Co-Borrower Template ID"
        name="englishCosignTemplateId"
        id="englishCosignTemplateId"
        isRequired={!isConga}
        isDisabled={isConga}
        helpText={templateHelp}
      />

      <DocuSignTemplateIdField
        label="Spanish Template ID"
        name="spanishTemplateId"
        id="spanishTemplateId"
        isDisabled={isConga}
        helpText={templateHelp}
      />

      <DocuSignTemplateIdField
        label="Spanish Co-Borrower Template ID"
        name="spanishCosignTemplateId"
        id="spanishCosignTemplateId"
        isDisabled={isConga}
        helpText={templateHelp}
      />
    </Box>
  );
};
